import { FC, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  LinearProgress,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  Chip,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { MsdRiskLevel } from "src/app/logic/assessment/msd/constants";
import { getMsdScoresDetailItem } from "src/components/organisms/assessment/help/msdScoresDetail";

type MSDScoreBoardProps = {
  scores: {
    lowBackScore: {
      cautionZoneCount: number;
      hazardZoneCount: number;
    };
    handWristScore: {
      cautionZoneCount: number;
      hazardZoneCount: number;
    };
    neckShoulderScore: {
      cautionZoneCount: number;
      hazardZoneCount: number;
    };
    kneeScore: {
      cautionZoneCount: number;
      hazardZoneCount: number;
    };
    totalCautionZoneCount: number;
    totalHazardZoneCount: number;
    riskLevel: MsdRiskLevel;
  };
};

const getScoreColor = (cautionCount: number, hazardCount: number): string => {
  if (hazardCount >= 3) return "#B71C1C";
  if (hazardCount > 0 || cautionCount >= 4) return "#FFB74D";
  if (cautionCount > 0) return "#FFF176";
  return "#B2DFDB";
};

const BodyZoneScoreItem: FC<{
  label: string;
  cautionCount: number;
  hazardCount: number;
}> = ({ label, cautionCount, hazardCount }) => (
  <Box mb={1.5}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 0.5 }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={1}>
        {cautionCount > 0 && (
          <Chip
            label={`${cautionCount} Caution`}
            size="small"
            sx={{ bgcolor: "#FFF9C4", color: "#F57F17" }}
          />
        )}
        {hazardCount > 0 && (
          <Chip
            label={`${hazardCount} Hazard`}
            size="small"
            sx={{ bgcolor: "#FFCDD2", color: "#B71C1C" }}
          />
        )}
      </Stack>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(((cautionCount + hazardCount * 2) / 8) * 100, 100)}
      sx={{
        height: 6,
        borderRadius: 3,
        bgcolor: "grey.200",
        "& .MuiLinearProgress-bar": {
          bgcolor: getScoreColor(cautionCount, hazardCount),
        },
      }}
    />
  </Box>
);

export const MSDScoreBoard: FC<MSDScoreBoardProps> = ({ scores }) => {
  const [expanded, setExpanded] = useState(false);
  const detailItem = getMsdScoresDetailItem(scores.riskLevel);
  const IconComponent = detailItem.icon;

  return (
    <Grid item xs={12}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{ boxShadow: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            borderBottom: expanded ? 1 : 0,
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", pr: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <IconComponent sx={{ color: detailItem.color }} />
              <Box>
                <Typography variant="h6">{scores.riskLevel} Risk</Typography>
                <Typography variant="body2" color="text.secondary">
                  {scores.totalCautionZoneCount} Caution Factors,{" "}
                  {scores.totalHazardZoneCount} Hazard Factors
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <BodyZoneScoreItem
                label="Low Back"
                cautionCount={scores.lowBackScore.cautionZoneCount}
                hazardCount={scores.lowBackScore.hazardZoneCount}
              />
              <BodyZoneScoreItem
                label="Hand & Wrist"
                cautionCount={scores.handWristScore.cautionZoneCount}
                hazardCount={scores.handWristScore.hazardZoneCount}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <BodyZoneScoreItem
                label="Neck & Shoulder"
                cautionCount={scores.neckShoulderScore.cautionZoneCount}
                hazardCount={scores.neckShoulderScore.hazardZoneCount}
              />
              <BodyZoneScoreItem
                label="Knee"
                cautionCount={scores.kneeScore.cautionZoneCount}
                hazardCount={scores.kneeScore.hazardZoneCount}
              />
            </Grid>
          </Grid>

          <Alert
            severity={
              scores.riskLevel === MsdRiskLevel.LOW
                ? "success"
                : scores.riskLevel === MsdRiskLevel.MODERATE
                  ? "warning"
                  : "error"
            }
            sx={{ mt: 2 }}
          >
            <AlertTitle>Assessment Result</AlertTitle>
            {detailItem.detail}
          </Alert>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
